import React, { useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Bot, BotOff, Loader2, AlertTriangle } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// As durações que a tela oferece. "Personalizado" abre um campo de minutos, e
// "permanente" é a pausa sem prazo — que é como a pausa sempre funcionou.
const DURACOES = [
	{ valor: "30", rotulo: "30 minutos" },
	{ valor: "60", rotulo: "1 hora" },
	{ valor: "120", rotulo: "2 horas" },
	{ valor: "240", rotulo: "4 horas" },
	{ valor: "480", rotulo: "8 horas" },
	{ valor: "1440", rotulo: "24 horas" },
	{ valor: "permanente", rotulo: "Até eu reativar" },
	{ valor: "personalizado", rotulo: "Personalizado…" },
];

// Quanto falta, em português. O servidor não avisa quando o prazo vence — ele
// limpa a pausa na próxima mensagem que chegar —, então a conta é feita aqui.
const faltando = (ate) => {
	const restante = new Date(ate).getTime() - Date.now();

	if (restante <= 0) return "o prazo já venceu";

	const minutos = Math.round(restante / 60000);

	if (minutos < 60) return `faltam ${minutos} min`;

	const horas = Math.floor(minutos / 60);
	const resto = minutos % 60;

	return resto
		? `faltam ${horas}h${String(resto).padStart(2, "0")}`
		: `faltam ${horas}h`;
};

const TicketAutomationModal = ({ open, onClose, ticket, onSaved }) => {
	const [fluxos, setFluxos] = useState([]);
	const [agentes, setAgentes] = useState([]);
	const [flowId, setFlowId] = useState("");
	const [aiAgentId, setAiAgentId] = useState("");
	const [duracao, setDuracao] = useState("60");
	const [minutosPersonalizados, setMinutosPersonalizados] = useState(90);
	const [salvando, setSalvando] = useState(false);

	const pausado = Boolean(ticket?.botPaused);
	const ate = ticket?.botPausedUntil;

	useEffect(() => {
		if (!open) return;

		setFlowId(ticket?.flowId ? String(ticket.flowId) : "nenhum");
		setAiAgentId(ticket?.aiAgentId ? String(ticket.aiAgentId) : "nenhum");

		(async () => {
			// Cada lista cai sozinha: sem o módulo de IA, /ai/agents dá 403 e
			// com Promise.all isso esvaziava também os fluxos. Sem as listas o
			// modal ainda serve para pausar.
			const [f, a] = await Promise.allSettled([
				api.get("/flows"),
				api.get("/ai/agents"),
			]);
			if (f.status === "fulfilled") {
				setFluxos(f.value.data?.flows || f.value.data || []);
			}
			if (a.status === "fulfilled") {
				setAgentes(a.value.data?.agents || a.value.data || []);
			}
		})();
	}, [open, ticket]);

	const alternarPausa = async () => {
		setSalvando(true);
		try {
			// Reativar não manda duração; pausar manda, salvo no permanente.
			const minutos =
				duracao === "permanente"
					? null
					: duracao === "personalizado"
						? Number(minutosPersonalizados) || null
						: Number(duracao);

			const { data } = await api.put(`/tickets/${ticket.id}/bot`, {
				paused: !pausado,
				duracaoMinutos: pausado ? undefined : minutos,
			});

			toast.success(
				pausado ? "Robô liberado nesta conversa." : "Robô pausado."
			);
			if (onSaved) onSaved(data);
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const salvarAutomacao = async () => {
		setSalvando(true);
		try {
			const { data } = await api.put(`/tickets/${ticket.id}/automation`, {
				flowId: flowId === "nenhum" ? null : Number(flowId),
				aiAgentId: aiAgentId === "nenhum" ? null : Number(aiAgentId),
			});

			toast.success("Automação desta conversa atualizada.");
			if (onSaved) onSaved(data);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Automação desta conversa</DialogTitle>
				</DialogHeader>

				<div className="space-y-4">
					{/* Estado atual, em uma frase — é o que a pessoa vem conferir. */}
					<div className="rounded-lg border p-3 text-sm">
						<p className="flex items-center gap-2 font-medium">
							{pausado ? (
								<BotOff className="h-4 w-4 text-amber-500" />
							) : (
								<Bot className="h-4 w-4 text-emerald-500" />
							)}
							{pausado ? "Robô pausado aqui" : "Robô respondendo aqui"}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							{pausado
								? ate
									? `Volta sozinho quando o prazo acabar — ${faltando(ate)}.`
									: "Sem prazo: só volta quando alguém reativar."
								: "Fluxo, bot de departamento e IA podem responder nesta conversa."}
						</p>
					</div>

					{!pausado && (
						<div className="space-y-1.5">
							<Label>Pausar por quanto tempo</Label>
							<Select value={duracao} onValueChange={setDuracao}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{DURACOES.map((d) => (
										<SelectItem key={d.valor} value={d.valor}>
											{d.rotulo}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{duracao === "personalizado" && (
								<Input
									type="number"
									min={1}
									aria-label="Minutos"
									value={minutosPersonalizados}
									onChange={(e) =>
										setMinutosPersonalizados(e.target.value)
									}
								/>
							)}

							<p className="text-xs text-muted-foreground">
								O prazo é conferido quando chega mensagem: passado o tempo, o
								robô volta sozinho.
							</p>
						</div>
					)}

					<Button
						type="button"
						variant={pausado ? "default" : "secondary"}
						className="w-full"
						disabled={salvando}
						onClick={alternarPausa}
					>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{pausado ? "Reativar o robô agora" : "Pausar o robô"}
					</Button>

					<div className="space-y-1.5 border-t pt-4">
						<Label>Fluxo desta conversa</Label>
						<Select value={flowId || undefined} onValueChange={setFlowId}>
							<SelectTrigger>
								<SelectValue placeholder="Usar o fluxo da conexão" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="nenhum">Usar o fluxo da conexão</SelectItem>
								{fluxos.map((f) => (
									<SelectItem key={f.id} value={String(f.id)}>
										{f.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							Escolhido aqui, ele tem prioridade sobre o da conexão — só nesta
							conversa.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label>Agente de IA desta conversa</Label>
						<Select
							value={aiAgentId || undefined}
							onValueChange={setAiAgentId}
						>
							<SelectTrigger>
								<SelectValue placeholder="Usar o agente da conexão" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="nenhum">Usar o agente da conexão</SelectItem>
								{agentes.map((a) => (
									<SelectItem key={a.id} value={String(a.id)}>
										{a.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
							<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							A IA continua desligada se a conexão estiver com ela desligada —
							escolher o agente aqui não liga o que está desligado lá.
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						disabled={salvando}
					>
						Fechar
					</Button>
					<Button type="button" onClick={salvarAutomacao} disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						Salvar
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default TicketAutomationModal;
